import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Clock, CheckCircle, Trophy, Users, Sparkles, Loader2, AlertCircle, ChevronLeft, ChevronRight, Zap, Star, Send, ShieldAlert, MonitorUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { getStudentSession, clearStudentSession } from '../../utils/supabase/auth';
import { supabase } from '../../utils/supabase/client';
import {
  getSession,
  getLessonSlides,
  submitAnswer,
  updateParticipantProgress,
  getSessionByCode,
  logSessionSecurityIncident
} from '../../utils/supabase/queries';
import type { Session, Slide } from '../../utils/supabase/client';
import { subscribeToSession, unsubscribeFromSession } from '../../utils/supabase/realtime';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { StudentSlideView } from '../components/slides';
import { isContentSlideType } from '../../utils/slides/slideRegistry';

type ActivityState = 'waiting' | 'active' | 'submitted' | 'results';
type SecurityLockReason = 'fullscreen' | 'tab-switch' | 'screenshot' | 'app-switch' | null;

const restoresActivityViewFromSubmission = (slideType: Slide['type']) => slideType === 'time_to_climb';
const activityOwnsPostSubmitView = (slideType: Slide['type']) => slideType === 'time_to_climb';

const BLOCKED_SHORTCUTS = new Set(['t', 'n', 'w', 'p', 's']);

const isScreenshotShortcut = (event: KeyboardEvent) => {
  const key = event.key.toLowerCase();
  const code = event.code?.toLowerCase() ?? '';
  const isPrintScreenKey = key === 'printscreen' || key === 'snapshot' || code === 'printscreen';
  const isMacScreenshotShortcut = event.metaKey && event.shiftKey && ['3', '4', '5'].includes(key);
  const isBrowserSaveShortcut = (event.ctrlKey || event.metaKey) && event.shiftKey && key === 's';
  const isAltPrintScreen = event.altKey && isPrintScreenKey;

  return isPrintScreenKey || isMacScreenshotShortcut || isBrowserSaveShortcut || isAltPrintScreen;
};

const isMobileSecureSession = () => {
  if (typeof navigator === 'undefined') return false;

  return /android|iphone|ipad|ipod/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1;
};

const SECURITY_COPY: Record<Exclude<SecurityLockReason, null>, { title: string; message: string }> = {
  fullscreen: {
    title: 'Fullscreen required',
    message: 'Return to fullscreen to continue the activity.'
  },
  'tab-switch': {
    title: 'Activity locked',
    message: 'Switching tabs or windows is not allowed during this activity.'
  },
  'app-switch': {
    title: 'Activity locked',
    message: 'Leaving the activity app or browser is not allowed during this session.'
  },
  screenshot: {
    title: 'Screenshot blocked',
    message: 'Screenshots are restricted during this activity.'
  }
};

export default function StudentLiveView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  // Session state
  const [session, setSession] = useState<Session | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(true);
  const [sessionMode, setSessionMode] = useState<'live' | 'student-paced'>('live');

  // Activity state
  const [activityState, setActivityState] = useState<ActivityState>('waiting');
  const [selectedAnswer, setSelectedAnswer] = useState<any>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastIsCorrect, setLastIsCorrect] = useState<boolean | null>(null);

  // Real-time
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);
  const [usePolling, setUsePolling] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [securityLockReason, setSecurityLockReason] = useState<SecurityLockReason>('fullscreen');
  const [secureModeReady, setSecureModeReady] = useState(false);
  const [securityIncidentCount, setSecurityIncidentCount] = useState(0);

  // Debounce timer for self-managed slide progress updates (e.g. collaborate_board)
  const progressDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSubmittingRef = useRef(false);
  const toastCooldownRef = useRef(0);
  const lastSecurityIncidentRef = useRef<{ reason: Exclude<SecurityLockReason, null>; at: number } | null>(null);

  // Load session on mount
  useEffect(() => {
    if (sessionId) {
      initializeSession();
    }

    return () => {
      // Cleanup
      if (resultsTimeoutRef.current) {
        clearTimeout(resultsTimeoutRef.current);
      }
      if (channel) {
        unsubscribeFromSession(channel);
      }
    };
  }, [sessionId]);

  // Check if student has already submitted for current slide
  useEffect(() => {
    if (session && slides[currentSlide] && studentName) {
      checkExistingSubmission();
    }
  }, [currentSlide, slides, studentName]);

  // Polling fallback when realtime fails
  useEffect(() => {
    if (!usePolling || !sessionId) return;

    console.log('🔄 Using polling mode for session updates');
    
    const pollInterval = setInterval(async () => {
      try {
        // Use session code for polling to ensure RLS compatibility
        const studentSession = getStudentSession();
        if (!studentSession?.sessionCode) return;

        const sessionData = await getSessionByCode(studentSession.sessionCode);

        // Check if session has been ended by teacher
        if (sessionData.status === 'ended') {
          console.log('📢 Session ended by teacher (polling)');
          setSessionEnded(true);
          clearStudentSession();
          clearInterval(pollInterval);
          return;
        }

        if (sessionData.current_slide !== currentSlide) {
          console.log('🔄 Slide changed via polling:', sessionData.current_slide);
          if (resultsTimeoutRef.current) {
            clearTimeout(resultsTimeoutRef.current);
            resultsTimeoutRef.current = null;
          }
          setCurrentSlide(sessionData.current_slide);
          setActivityState('active');
          setSelectedAnswer(null);
          setHasSubmitted(false);
          setLastIsCorrect(null);
        }
      } catch (error) {
        // If getSessionByCode fails (session no longer active), treat as ended
        console.log('📢 Session no longer available (polling)');
        setSessionEnded(true);
        clearStudentSession();
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [sessionId, usePolling, currentSlide]);

  useEffect(() => {
    if (!session || loading || sessionEnded) return;

    const currentSlideId = slides[currentSlide]?.id ?? null;
    const mobileSecureSession = isMobileSecureSession();

    const notifySecurityEvent = (reason: Exclude<SecurityLockReason, null>) => {
      const now = Date.now();
      if (now - toastCooldownRef.current < 1500) return;
      toastCooldownRef.current = now;
      toast.error(SECURITY_COPY[reason].message);
    };

    const recordSecurityIncident = (reason: Exclude<SecurityLockReason, null>, source: string) => {
      if (reason === 'fullscreen' || !sessionId || !studentName) return;

      const now = Date.now();
      const lastIncident = lastSecurityIncidentRef.current;
      if (lastIncident && lastIncident.reason === reason && now - lastIncident.at < 1500) {
        return;
      }

      lastSecurityIncidentRef.current = { reason, at: now };
      setSecurityIncidentCount((count) => count + 1);

      void logSessionSecurityIncident({
        sessionId,
        studentName,
        slideId: currentSlideId,
        incidentType: reason,
        metadata: {
          slideIndex: currentSlide,
          path: window.location.pathname,
          userAgent: navigator.userAgent,
          visibilityState: document.visibilityState,
          timestamp: new Date().toISOString(),
          source,
          deviceType: mobileSecureSession ? 'mobile' : 'desktop'
        }
      }).catch((error) => {
        console.error('Error logging security incident:', error);
      });
    };

    const lockActivity = (reason: Exclude<SecurityLockReason, null>, source = 'unknown') => {
      setSecureModeReady(false);
      setSecurityLockReason(reason);
      recordSecurityIncident(reason, source);
      notifySecurityEvent(reason);
    };

    const syncFullscreenState = () => {
      if (mobileSecureSession) {
        setSecureModeReady(!document.hidden);
        if (!document.hidden) {
          setSecurityLockReason(null);
        }
        return;
      }

      const isFullscreen = Boolean(document.fullscreenElement);
      if (isFullscreen) {
        setSecureModeReady(!document.hidden);
        if (!document.hidden) {
          setSecurityLockReason(null);
        }
        return;
      }

      lockActivity('fullscreen', 'fullscreenchange');
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        lockActivity(mobileSecureSession ? 'app-switch' : 'tab-switch', 'visibilitychange');
      } else if (mobileSecureSession || document.fullscreenElement) {
        setSecureModeReady(true);
        setSecurityLockReason(null);
      }
    };

    const handleWindowBlur = () => {
      lockActivity(mobileSecureSession ? 'app-switch' : 'tab-switch', 'blur');
    };

    const handlePageHide = () => {
      lockActivity(mobileSecureSession ? 'app-switch' : 'tab-switch', 'pagehide');
    };

    const handleFreeze = () => {
      lockActivity(mobileSecureSession ? 'app-switch' : 'tab-switch', 'freeze');
    };

    const handleScreenshotAttempt = (event: KeyboardEvent) => {
      if (!isScreenshotShortcut(event)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      lockActivity('screenshot', 'keyboard');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const blocksBrowserShortcut = (event.ctrlKey || event.metaKey) && BLOCKED_SHORTCUTS.has(key);
      const blocksPrivateWindow = event.shiftKey && (key === 'n' || key === 'p');

      if (blocksBrowserShortcut || blocksPrivateWindow) {
        event.preventDefault();
        lockActivity('tab-switch');
        return;
      }

      handleScreenshotAttempt(event);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      handleScreenshotAttempt(event);
    };

    const preventContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('freeze', handleFreeze as EventListener);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('contextmenu', preventContextMenu);
    window.addEventListener('beforeunload', handleBeforeUnload);

    syncFullscreenState();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('freeze', handleFreeze as EventListener);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('contextmenu', preventContextMenu);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentSlide, loading, session, sessionEnded, sessionId, slides, studentName]);

  const enterSecureMode = async () => {
    try {
      if (isMobileSecureSession()) {
        setSecureModeReady(!document.hidden);
        setSecurityLockReason(document.hidden ? 'app-switch' : null);
        return;
      }

      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      }

      setSecureModeReady(true);
      setSecurityLockReason(null);
    } catch (error) {
      console.error('Error entering secure mode:', error);
      setSecureModeReady(false);
      setSecurityLockReason('fullscreen');
      toast.error('Fullscreen access is required before the activity can continue.');
    }
  };

  const initializeSession = async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      // Get student session from localStorage
      const studentSession = getStudentSession();
      if (!studentSession || studentSession.sessionId !== sessionId) {
        toast.error('Please join the session first');
        navigate('/join');
        return;
      }

      setStudentName(studentSession.studentName);

      // Load session data - use session code for better RLS compatibility
      let sessionData;
      if (studentSession.sessionCode) {
        console.log('📡 Fetching session by code:', studentSession.sessionCode);
        sessionData = await getSessionByCode(studentSession.sessionCode);
      } else {
        // Fallback: if no session code, user might be from an old session
        // This shouldn't happen in normal flow, but handle gracefully
        toast.error('Session code missing. Please rejoin the session.');
        navigate('/join');
        return;
      }
      
      // Verify session is active
      if (sessionData.status !== 'active') {
        toast.error('This session is not active');
        navigate('/student/dashboard');
        return;
      }

      setSession(sessionData);

      // Load slides
      const slidesData = await getLessonSlides(sessionData.lesson_id);
      setSlides(slidesData);

      // Set current slide from session
      setCurrentSlide(sessionData.current_slide || 0);
      setLastIsCorrect(null);

      // Set session mode
      setSessionMode(sessionData.mode || 'live');

      console.log('📊 Session loaded:', sessionData);
      console.log('📚 Slides loaded:', slidesData.length, 'slides');
      console.log('🎓 Session mode:', sessionData.mode);

      // Try to subscribe to real-time updates, but fallback to polling if it fails
      // Only for live sessions - student-paced doesn't need real-time updates
      let realtimeWorking = false;

      if (sessionData.mode === 'live') {
        const realtimeChannel = subscribeToSession(sessionId, {
          onSlideChange: (payload) => {
            console.log('✅ Realtime: Slide changed to:', payload.current_slide);
            if (resultsTimeoutRef.current) {
              clearTimeout(resultsTimeoutRef.current);
              resultsTimeoutRef.current = null;
            }
            setCurrentSlide(payload.current_slide);
            setActivityState('active');
            setSelectedAnswer(null);
            setHasSubmitted(false);
            setLastIsCorrect(null);
          },
          onNewSubmission: () => {
            // Other student submitted
          },
          onNewParticipant: () => {
            // New participant joined
          },
          onSessionEnd: () => {
            console.log('📢 Session ended by teacher (realtime)');
            setSessionEnded(true);
            clearStudentSession();
          }
        });

        setChannel(realtimeChannel);

        // Wait a bit to see if subscription succeeds, otherwise use polling
        setTimeout(() => {
          if (!realtimeWorking) {
            console.log('⚠️ Realtime subscription failed, using polling fallback');
            setUsePolling(true);
          }
        }, 3000);
      }

      setActivityState('active');
    } catch (error) {
      console.error('Error initializing session:', error);
      toast.error('Failed to load session');
      navigate('/join');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSubmission = async () => {
    if (!sessionId || !slides[currentSlide] || !studentName) return;

    const currentSlideType = slides[currentSlide].type;
    
    // Non-interactive slides (content-only) don't require submission
    const nonInteractiveTypes = ['text_image', 'video', 'pdf', 'web_content', 'field_trip', 'audio'];
    if (nonInteractiveTypes.includes(currentSlideType)) {
      setHasSubmitted(true);
      setActivityState('active');
      // Update progress for content slides so Avg Progress reflects viewing them
      if (sessionId && studentName && slides.length > 0) {
        const progressPercentage = Math.round(((currentSlide + 1) / slides.length) * 100);
        updateParticipantProgress(sessionId, studentName, progressPercentage).catch(err => {
          console.error('Error updating progress for content slide:', err);
        });
      }
      return;
    }

    try {
      const { data, error } = await supabase
        .from('submissions')
        .select('id, score, graded, answer_data, is_correct')
        .eq('session_id', sessionId)
        .eq('slide_id', slides[currentSlide].id)
        .eq('student_name', studentName)
        .maybeSingle();

      if (data) {
        setHasSubmitted(true);
        setLastIsCorrect(data.is_correct ?? null);
        // For collaborate_board, keep the board interactive even if a submission exists
        // (students can keep adding/deleting notes)
        if (currentSlideType === 'collaborate_board') {
          setActivityState('active');
          // Sync progress for collaborate_board since it manages its own submissions
          // and may not have updated progress_percentage on prior interactions
          if (sessionId && studentName && slides.length > 0) {
            const progressPercentage = Math.round(((currentSlide + 1) / slides.length) * 100);
            updateParticipantProgress(sessionId, studentName, progressPercentage).catch(err => {
              console.error('Error syncing progress for collaborate_board:', err);
            });
          }
        } else if (restoresActivityViewFromSubmission(currentSlideType)) {
          setSelectedAnswer(data.answer_data ?? null);
          setActivityState('active');
        } else {
          setActivityState('submitted');
        }
      }
    } catch (error) {
      console.error('Error checking submission:', error);
    }
  };

  const handleAnswerSelect = (answer: any) => {
    const currentSlideData = slides[currentSlide];
    // Allow collaborate_board to keep updating even after initial submission
    // (students can continue adding/deleting notes)
    const isSelfManaged = currentSlideData?.type === 'collaborate_board';
    if (activityState === 'active' && (!hasSubmitted || isSelfManaged)) {
      setSelectedAnswer(answer);

      // For self-managed slides like collaborate_board, update participant progress
      // when the student interacts (e.g. adds a note), since they don't go through
      // the normal submit flow. Debounced to avoid excessive DB calls.
      if (isSelfManaged && sessionId && studentName && slides.length > 0) {
        if (progressDebounceRef.current) {
          clearTimeout(progressDebounceRef.current);
        }
        progressDebounceRef.current = setTimeout(() => {
          const progressPercentage = Math.round(((currentSlide + 1) / slides.length) * 100);
          updateParticipantProgress(sessionId, studentName, progressPercentage).catch(err => {
            console.error('Error updating progress for self-managed slide:', err);
          });
        }, 2000);
      }
    }
  };

  const handleSubmit = async (providedAnswer?: any) => {
    if (isSubmittingRef.current) return;

    if (!sessionId || !slides[currentSlide] || !studentName) {
      toast.error('Please select an answer');
      return;
    }

    const currentSlideData = slides[currentSlide];
    
    // Prefer providedAnswer from self-submitting activities, otherwise use selectedAnswer
    const answerToUse = providedAnswer !== undefined ? providedAnswer : selectedAnswer;
    
    if (answerToUse === null && currentSlideData.type !== 'draw_it' && currentSlideData.type !== 'collaborate_board') {
      toast.error('Please select an answer');
      return;
    }

    const usesSharedSubmittedScreen = [
      'multiple_choice',
      'true_false',
      'poll',
      'open_ended',
      'fill_blanks',
      'drag_drop',
      'sequencing',
      'label_diagram',
    ].includes(currentSlideData.type);
    const usesSharedResultsScreen = [
      'multiple_choice',
      'true_false',
      'fill_blanks',
      'drag_drop',
    ].includes(currentSlideData.type);
    const ownsPostSubmitView = activityOwnsPostSubmitView(currentSlideData.type);

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // Prepare answer data based on slide type
      let answerData: any = {};
      let isCorrect: boolean | null = null;

      switch (currentSlideData.type) {
        case 'multiple_choice':
          answerData = { selectedOption: selectedAnswer, answer: selectedAnswer };
          isCorrect = currentSlideData.content?.correctAnswer != null
            ? selectedAnswer === currentSlideData.content.correctAnswer
            : null;
          break;

        case 'true_false':
          answerData = { answer: selectedAnswer };
          isCorrect = currentSlideData.content?.correctAnswer != null
            ? selectedAnswer === currentSlideData.content.correctAnswer
            : null;
          break;

        case 'quiz': {
          // Quiz is a self-submitting multi-question activity.
          // answerToUse is Record<number, number> from Quiz component's onSubmit(answers)
          const quizAnswers = (answerToUse as any)?.answers ?? answerToUse;
          const quizQuestions = currentSlideData.content?.questions || [];
          answerData = { answers: quizAnswers };
          if (quizQuestions.length > 0 && quizAnswers && typeof quizAnswers === 'object') {
            const quizCorrectCount = quizQuestions.reduce(
              (count: number, q: { correctAnswer: number }, i: number) =>
                quizAnswers[i] === q.correctAnswer ? count + 1 : count,
              0
            );
            isCorrect = quizCorrectCount === quizQuestions.length;
          } else {
            isCorrect = null;
          }
          break;
        }

        case 'poll':
          answerData = { selectedOption: selectedAnswer };
          isCorrect = null; // Polls don't have correct answers
          break;

        case 'open_ended':
          answerData = { answer: selectedAnswer };
          isCorrect = null; // Requires manual grading
          break;

        case 'word_cloud':
          answerData = { words: selectedAnswer };
          isCorrect = null;
          break;

        case 'fill_blanks': {
          answerData = { answers: selectedAnswer };
          const correctAnswers = currentSlideData.content?.answers || [];
          if (correctAnswers.length > 0 && selectedAnswer) {
            // Count how many blanks are correct for partial credit
            let fbCorrectCount = 0;
            correctAnswers.forEach((answer: string, i: number) => {
              if ((selectedAnswer[i] || '').trim().toLowerCase() === answer.trim().toLowerCase()) {
                fbCorrectCount++;
              }
            });
            isCorrect = fbCorrectCount === correctAnswers.length;
            // Store partial score info for calculatedScore
            const fbMaxPts = currentSlideData.content?.points ?? currentSlideData.content?.maxPoints ?? 10;
            (answerData as any).__partialScore = Math.round((fbCorrectCount / correctAnswers.length) * fbMaxPts);
          } else {
            isCorrect = null;
          }
          break;
        }

        case 'label_diagram': {
          answerData = { placements: answerToUse || [] };
          isCorrect = null;
          break;
        }

        case 'time_to_climb': {
          const questions = currentSlideData.content?.questions || [];
          const climbQuizType = currentSlideData.content?.quizType || 'multiple_choice';
          const providedAnswers = (answerToUse as any)?.answers ?? answerToUse;
          answerData = { answers: providedAnswers };
          if (questions.length > 0 && providedAnswers && typeof providedAnswers === 'object') {
            const correctCount = questions.reduce(
              (count: number, q: { correctAnswer: number | string }, i: number) => {
                if (climbQuizType === 'identification') {
                  const student = String(providedAnswers[i] || '').trim().toLowerCase();
                  const correct = String(q.correctAnswer || '').trim().toLowerCase();
                  return student === correct ? count + 1 : count;
                }
                return providedAnswers[i] === q.correctAnswer ? count + 1 : count;
              },
              0
            );
            isCorrect = correctCount === questions.length;
          } else {
            isCorrect = null;
          }
          break;
        }

        case 'matching_pairs': {
          const pairs = Array.isArray(currentSlideData.content?.pairs)
            ? currentSlideData.content.pairs
            : null;
          const leftItems = Array.isArray(currentSlideData.content?.leftItems)
            ? currentSlideData.content.leftItems
            : null;
          const expectedMatches: Record<string, string> = {};

          if (leftItems && Array.isArray(currentSlideData.content?.rightItems)) {
            leftItems.forEach((item: any, index: number) => {
              const leftId = item.id ?? `left-${index}`;
              expectedMatches[leftId] = item.match ?? `right-${index}`;
            });
          } else if (pairs) {
            pairs.forEach((_: any, index: number) => {
              expectedMatches[`left-${index}`] = `right-${index}`;
            });
          }

          const submittedMatches = (answerToUse as any)?.matches || answerToUse || {};
          const total = Object.keys(expectedMatches).length;
          const correctCount = Object.keys(expectedMatches).reduce(
            (count, leftId) => (submittedMatches[leftId] === expectedMatches[leftId] ? count + 1 : count),
            0
          );

          answerData = { matches: submittedMatches, correctCount, total };
          isCorrect = total > 0 ? correctCount === total : null;
          break;
        }

        case 'draw_it':
          // For draw_it, use providedAnswer if available, otherwise fall back to selectedAnswer
          answerData = { drawing: answerToUse?.drawing || answerToUse };
          isCorrect = null; // Requires manual grading
          break;

        case 'drag_drop': {
          // DragDrop stores placements as { [itemId]: targetId }
          // Grade calculator reads submission.answer[item.id] directly
          const dragItems = currentSlideData.content?.items || [];
          const studentPlacements = answerToUse || {};
          answerData = studentPlacements;
          let ddCorrect = 0;
          dragItems.forEach((item: any) => {
            if (studentPlacements[item.id] === item.targetId) ddCorrect++;
          });
          isCorrect = dragItems.length > 0 ? ddCorrect === dragItems.length : null;
          break;
        }

        case 'sequencing': {
          // Sequencing stores { order: string[] } — array of item IDs in student's order
          // Grade calculator reads submission.answer.order
          const seqStudentOrder = answerToUse || [];
          answerData = { order: seqStudentOrder };
          // Sequencing is graded server-side; don't show grade animation on submit
          isCorrect = null;
          break;
        }

        case 'collaborate_board':
          // CollaborateBoard manages its own submissions via serverUpsertSubmission.
          // Skip overwriting the existing submission, but still update participant
          // progress so Avg Progress and Top Performers stay in sync.
          {
            const progressPercentage = Math.round(((currentSlide + 1) / slides.length) * 100);
            await updateParticipantProgress(sessionId, studentName, progressPercentage);
          }
          return;

        default:
          answerData = { answer: selectedAnswer };
          isCorrect = null;
      }

      setLastIsCorrect(isCorrect);

      // Submit answer
      // Resolve max points from slide content, falling back to 10
      const slideMaxPoints = currentSlideData.content?.points ?? currentSlideData.content?.maxPoints ?? 10;

      // Extract partial score for fill_blanks (if calculated)
      const partialScore = (answerData as any).__partialScore;
      if ((answerData as any).__partialScore !== undefined) {
        delete (answerData as any).__partialScore;
      }

      await submitAnswer({
        session_id: sessionId,
        slide_id: currentSlideData.id,
        student_name: studentName,
        answer_data: answerData,
        is_correct: isCorrect,
        maxPoints: slideMaxPoints,
        ...(partialScore !== undefined ? { calculatedScore: partialScore } : {})
      });

      setHasSubmitted(true);
      toast.success('Answer submitted!');

      if (usesSharedSubmittedScreen) {
        setActivityState('submitted');
      }

      // Update participant progress
      const progressPercentage = Math.round(((currentSlide + 1) / slides.length) * 100);
      try {
        await updateParticipantProgress(sessionId, studentName, progressPercentage);
      } catch (error) {
        console.error('Answer was saved, but participant progress could not be updated:', error);
      }

      if (resultsTimeoutRef.current) {
        clearTimeout(resultsTimeoutRef.current);
        resultsTimeoutRef.current = null;
      }

      if (ownsPostSubmitView) {
        setActivityState('active');
      }

      if (!usesSharedSubmittedScreen) {
        setActivityState('active');
        return;
      }

      // Show results if auto-gradeable
      if (usesSharedResultsScreen && isCorrect !== null) {
        resultsTimeoutRef.current = setTimeout(() => {
          setActivityState('results');
          resultsTimeoutRef.current = null;
        }, 1000);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to submit answer');
      setActivityState('active');
      setLastIsCorrect(null);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Navigation functions for student-paced lessons
  const handleNextSlide = () => {
    if (currentSlide < slides.length - 1) {
      if (resultsTimeoutRef.current) {
        clearTimeout(resultsTimeoutRef.current);
        resultsTimeoutRef.current = null;
      }
      const nextSlide = currentSlide + 1;
      setCurrentSlide(nextSlide);
      setActivityState('active');
      setSelectedAnswer(null);
      setHasSubmitted(false);
      setLastIsCorrect(null);
      // Update progress
      const progressPercentage = Math.round(((nextSlide + 1) / slides.length) * 100);
      updateParticipantProgress(sessionId!, studentName, progressPercentage).catch(err => {
        console.error('Error updating progress:', err);
      });
    } else {
      toast.success('Lesson completed!');
      navigate('/student/dashboard');
    }
  };

  const handlePreviousSlide = () => {
    if (currentSlide > 0) {
      if (resultsTimeoutRef.current) {
        clearTimeout(resultsTimeoutRef.current);
        resultsTimeoutRef.current = null;
      }
      setCurrentSlide(currentSlide - 1);
      setActivityState('active');
      setSelectedAnswer(null);
      setHasSubmitted(false);
      setLastIsCorrect(null);
    }
  };

  // Session ended by teacher - show message and allow joining another session
  if (sessionEnded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-indigo-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 150 }}
          className="text-center max-w-md"
        >
          {/* Animated ring */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
            className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center backdrop-blur-sm border border-white/10 shadow-2xl"
          >
            <motion.div
              initial={{ rotate: -20 }}
              animate={{ rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <AlertCircle className="w-16 h-16 text-white/90" />
            </motion.div>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">Session Ended</h2>
          <p className="text-base sm:text-lg text-white/70 mb-10 leading-relaxed">
            The teacher has ended this session. Great work today!
          </p>
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Button
              onClick={() => navigate('/join')}
              size="lg"
              className="px-10 py-4 text-lg bg-white text-slate-900 hover:bg-white/90 rounded-2xl shadow-xl font-semibold"
            >
              Join Another Session
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="relative w-16 h-16 mx-auto mb-6">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary" />
            </div>
          </div>
          <p className="text-muted-foreground font-medium text-lg">Loading session...</p>
          <p className="text-muted-foreground/60 text-sm mt-1">Getting things ready for you</p>
        </motion.div>
      </div>
    );
  }

  if (!session || !slides.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-muted/50 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold mb-2">Session not available</h2>
          <p className="text-muted-foreground mb-6 text-sm">This session may have ended or doesn't exist</p>
          <Button onClick={() => navigate('/join')} size="lg" className="rounded-2xl px-8">
            Join a Session
          </Button>
        </motion.div>
      </div>
    );
  }

  const currentSlideData = slides[currentSlide];
  const isContentSlide = isContentSlideType(currentSlideData.type);
  const usesSharedSubmittedScreen = [
    'multiple_choice',
    'true_false',
    'poll',
    'open_ended',
    'fill_blanks',
    'drag_drop',
    'sequencing',
    'label_diagram',
  ].includes(currentSlideData.type);
  const usesSharedResultsScreen = [
    'multiple_choice',
    'true_false',
    'fill_blanks',
    'drag_drop',
  ].includes(currentSlideData.type);
  const ownsPostSubmitView = activityOwnsPostSubmitView(currentSlideData.type);
  const secureOverlay = !secureModeReady && securityLockReason ? SECURITY_COPY[securityLockReason] : null;

  // Waiting state
  if (activityState === 'waiting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-primary to-violet-600 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-white/5 blur-3xl"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-white/5 blur-3xl"
          />
        </div>

        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center relative z-10"
        >
          {/* Animated icon with pulse rings */}
          <div className="relative w-28 h-28 mx-auto mb-8">
            <motion.div
              animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 rounded-full bg-white/10"
            />
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0, 0.2] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              className="absolute inset-0 rounded-full bg-white/10"
            />
            <div className="absolute inset-0 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm border border-white/20">
              <Users className="w-14 h-14 text-white" />
            </div>
          </div>

          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-3 tracking-tight">Get Ready!</h2>
          <p className="text-xl text-white/80 mb-8">Waiting for your teacher to start...</p>

          {/* Bouncing dots */}
          <div className="flex items-center justify-center gap-3">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                className="w-4 h-4 rounded-full bg-white/70"
              />
            ))}
          </div>

          {/* Student name pill */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-10 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-full border border-white/15"
          >
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/90 font-medium text-sm">{studentName} is connected</span>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Submitted state
  if (!ownsPostSubmitView && activityState === 'submitted' && usesSharedSubmittedScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Floating confetti-like elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ y: '110%', x: `${10 + i * 12}%`, opacity: 0.7 }}
              animate={{ y: '-10%', opacity: 0 }}
              transition={{ duration: 3 + i * 0.5, repeat: Infinity, delay: i * 0.4, ease: 'easeOut' }}
              className={`absolute w-3 h-3 rounded-full ${
                ['bg-white/30', 'bg-yellow-300/30', 'bg-pink-300/30', 'bg-blue-300/30'][i % 4]
              }`}
            />
          ))}
        </div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="text-center relative z-10"
        >
          {/* Success animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            className="w-28 h-28 mx-auto mb-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm border border-white/20"
          >
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 300, delay: 0.4 }}
            >
              <CheckCircle className="w-14 h-14 text-white" />
            </motion.div>
          </motion.div>
          <h2 className="text-4xl font-extrabold text-white mb-3 tracking-tight">Submitted!</h2>
          <p className="text-xl text-white/80">Waiting for next slide...</p>

          {/* Progress pulse */}
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="mt-8 w-48 h-1.5 mx-auto bg-white/20 rounded-full overflow-hidden"
          >
            <motion.div
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="w-1/3 h-full bg-white/60 rounded-full"
            />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Results state (for auto-graded questions)
  if (!ownsPostSubmitView && activityState === 'results' && usesSharedResultsScreen) {
    const isCorrect = lastIsCorrect === true;

    return (
      <div className={`min-h-screen ${
        isCorrect 
          ? 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600' 
          : 'bg-gradient-to-br from-orange-500 via-red-500 to-rose-600'
      } flex items-center justify-center p-4 relative overflow-hidden`}>
        {/* Celebration particles for correct answers */}
        {isCorrect && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  y: '50%', 
                  x: '50%',
                  scale: 0,
                }}
                animate={{ 
                  y: `${Math.random() * 100}%`,
                  x: `${Math.random() * 100}%`,
                  scale: [0, 1, 0],
                  rotate: [0, 360],
                }}
                transition={{ duration: 2, delay: i * 0.1, ease: 'easeOut' }}
                className={`absolute w-4 h-4 ${
                  ['bg-yellow-300', 'bg-white', 'bg-emerald-200', 'bg-cyan-200'][i % 4]
                } ${['rounded-full', 'rounded-sm rotate-45', 'rounded-full'][i % 3]}`}
              />
            ))}
          </div>
        )}

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="text-center relative z-10"
        >
          {/* Icon with celebration effect */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 10 }}
            className="w-36 h-36 mx-auto mb-8 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm border border-white/20"
          >
            {isCorrect ? (
              <motion.div
                animate={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Star className="w-20 h-20 text-yellow-300 fill-yellow-300" />
              </motion.div>
            ) : (
              <AlertCircle className="w-20 h-20 text-white/90" />
            )}
          </motion.div>

          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-5xl font-extrabold text-white mb-4 tracking-tight"
          >
            {isCorrect ? 'Correct!' : 'Not Quite'}
          </motion.h2>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-2xl text-white/90 mb-6">
              {isCorrect 
                ? (
                  <span className="flex items-center justify-center gap-2">
                    <Zap className="w-6 h-6 text-yellow-300" />
                    +{currentSlideData.content?.points || 10} points!
                    <Zap className="w-6 h-6 text-yellow-300" />
                  </span>
                )
                : 'Keep trying, you\'ll get it next time!'}
            </p>
          </motion.div>

          {!isCorrect && currentSlideData.content?.correctAnswer !== undefined && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-md mx-auto border border-white/10"
            >
              <p className="text-white/70 mb-2 text-sm font-medium uppercase tracking-wide">Correct answer</p>
              <p className="text-white text-xl font-bold">
                {currentSlideData.type === 'multiple_choice' || currentSlideData.type === 'quiz'
                  ? currentSlideData.content.options[currentSlideData.content.correctAnswer]
                  : currentSlideData.content.correctAnswer.toString()}
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }

  // Active state - Show question/activity
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/3 relative overflow-hidden">
      {studentName && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 overflow-hidden opacity-[0.06]"
        >
          <div className="absolute inset-[-20%] rotate-[-24deg] bg-[radial-gradient(circle_at_center,transparent_0,transparent_28px,rgba(15,23,42,0.9)_29px,transparent_30px)] [background-size:260px_160px]" />
          <div className="absolute inset-0 flex items-center justify-center text-[clamp(1.5rem,4vw,3rem)] font-black uppercase tracking-[0.45em] text-foreground/80">
            {studentName}
          </div>
        </div>
      )}

      {secureOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-md p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900/95 p-8 text-center shadow-2xl"
          >
            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-amber-500/15 text-amber-300">
              {securityLockReason === 'screenshot' ? <ShieldAlert className="h-10 w-10" /> : <MonitorUp className="h-10 w-10" />}
            </div>
            <h2 className="text-3xl font-black tracking-tight text-white">{secureOverlay.title}</h2>
            <p className="mt-3 text-base leading-relaxed text-white/70">{secureOverlay.message}</p>
            <p className="mt-2 text-sm text-white/45">Remain on this page in fullscreen mode to continue.</p>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm text-white/70">
              <p>Security events recorded: {securityIncidentCount}</p>
              <p>Student: {studentName || 'Unknown student'}</p>
            </div>
            <Button
              onClick={enterSecureMode}
              size="lg"
              className="mt-8 h-12 w-full rounded-2xl bg-amber-500 font-semibold text-slate-950 hover:bg-amber-400"
            >
              Enter Fullscreen And Continue
            </Button>
          </motion.div>
        </div>
      )}

      {/* Header — Nearpod-style floating glass header */}
      <header className="bg-card/80 backdrop-blur-lg border-b border-border/50 sticky top-0 z-20 shadow-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-3 sm:px-6 py-2.5 sm:py-3 gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="font-bold text-sm sm:text-base truncate">{(session as any).lessons?.title || (sessionMode === 'student-paced' ? 'Student-Paced Session' : 'Live Session')}</h1>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {/* Slide indicator pill */}
            <div className="hidden sm:flex items-center gap-1.5 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-semibold">
              <Zap className="w-3 h-3" />
              {currentSlide + 1} / {slides.length}
            </div>
            {/* Student avatar */}
            <div className="flex items-center gap-2 bg-muted/50 px-2.5 sm:px-3 py-1.5 rounded-full max-w-[45vw] sm:max-w-none">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">{studentName.charAt(0).toUpperCase()}</span>
              </div>
              <span className="text-xs font-medium truncate hidden sm:inline">{studentName}</span>
            </div>
          </div>
        </div>
        {/* Progress Bar — colored gradient */}
        <div className="h-1 bg-muted/30">
          <motion.div
            className="h-full bg-gradient-to-r from-primary via-accent to-primary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </header>

      {/* Content */}
      <div className="px-3 py-4 sm:p-6 pb-8">
        <div className="max-w-5xl mx-auto">
          {/* Slide type indicator for content slides */}
          {isContentSlide && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center mb-4"
            >
              <div className="inline-flex items-center gap-2 bg-muted/50 px-3 py-1 rounded-full text-xs font-medium text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                Content Slide
              </div>
            </motion.div>
          )}

          {/* Use the new StudentSlideView component */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3 }}
            >
              <StudentSlideView
                slide={currentSlideData}
                onSubmit={handleSubmit}
                onAnswerChange={handleAnswerSelect}
                selectedAnswer={selectedAnswer}
                hasSubmitted={hasSubmitted}
                shuffleSeed={`${sessionId ?? 'session'}:${studentName}`}
                sessionMode={sessionMode}
              />
            </motion.div>
          </AnimatePresence>

          {/* Submit / Wait Section */}
          <div className="text-center space-y-3 sm:space-y-4 mt-6">
            {(() => {
              const isNonInteractive = isContentSlide;
              const hasSelfSubmit = [
                'draw_it',
                'time_to_climb',
                'collaborate_board',
                'matching_pairs',
                'quiz',
                'word_cloud'
              ].includes(currentSlideData.type);

              // For student-paced mode
              if (sessionMode === 'student-paced') {
                return (
                  <div className="space-y-3 sm:space-y-4">
                    {isNonInteractive && !hasSubmitted && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-muted/30 rounded-2xl p-4 text-muted-foreground text-base"
                      >
                        <p>📚 Review the content, then continue</p>
                      </motion.div>
                    )}
                    {!isNonInteractive && !hasSelfSubmit && !hasSubmitted && (
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          onClick={() => handleSubmit()}
                          disabled={selectedAnswer === null}
                          size="lg"
                          className="w-full sm:w-auto px-8 sm:px-12 rounded-2xl h-12 text-base font-semibold shadow-lg shadow-primary/20 bg-gradient-to-r from-primary to-primary/90"
                        >
                          <Send className="w-5 h-5 mr-2" />
                          Submit Answer
                        </Button>
                      </motion.div>
                    )}

                    {/* Navigation controls for student-paced — Nearpod style */}
                    <div className="mt-6 flex flex-col gap-3 border-t border-border/40 pt-6 sm:flex-row sm:items-center sm:justify-between">
                      <Button
                        onClick={handlePreviousSlide}
                        disabled={currentSlide === 0}
                        variant="outline"
                        className="rounded-2xl px-5 h-10 text-sm font-medium w-full sm:w-auto"
                        size="sm"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <div className="flex items-center gap-1.5">
                        {slides.map((_, idx) => (
                          <div
                            key={idx}
                            className={`w-2 h-2 rounded-full transition-all ${
                              idx === currentSlide
                                ? 'bg-primary w-5'
                                : idx < currentSlide
                                ? 'bg-primary/40'
                                : 'bg-muted-foreground/20'
                            }`}
                          />
                        ))}
                      </div>
                      <Button
                        onClick={handleNextSlide}
                        disabled={currentSlide === slides.length - 1 && !hasSubmitted && !isNonInteractive}
                        className="rounded-2xl px-5 h-10 text-sm font-medium w-full sm:w-auto"
                        size="sm"
                      >
                        {currentSlide === slides.length - 1 ? 'Finish' : 'Next'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                );
              }

              // For live mode (original behavior)
              if (isNonInteractive) {
                return (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-muted/30 rounded-2xl p-4 text-muted-foreground text-base"
                  >
                    <p>📚 Review the content and wait for the teacher to advance</p>
                  </motion.div>
                );
              }

              if (hasSelfSubmit) return null;

              return (
                <motion.div whileHover={!hasSubmitted ? { scale: 1.02 } : {}} whileTap={!hasSubmitted ? { scale: 0.98 } : {}}>
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={selectedAnswer === null || hasSubmitted}
                    size="lg"
                    className={`px-8 sm:px-12 w-full sm:w-auto rounded-2xl h-12 text-base font-semibold transition-all ${
                      hasSubmitted
                        ? 'bg-emerald-500 hover:bg-emerald-500'
                        : 'shadow-lg shadow-primary/20 bg-gradient-to-r from-primary to-primary/90'
                    }`}
                  >
                    {hasSubmitted ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Submitted
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Answer
                      </>
                    )}
                  </Button>
                </motion.div>
              );
            })()}
          </div>
        </div>
      </div>
      {isSubmitting && (
        <motion.div
          role="status"
          aria-live="assertive"
          aria-label="Submitting answer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="w-full max-w-sm rounded-2xl border border-border bg-card p-7 text-center shadow-2xl"
          >
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-primary" />
            <p className="text-lg font-bold">Submitting your answer...</p>
            <p className="mt-1 text-sm text-muted-foreground">Please wait while we save it.</p>
            <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-primary/15">
              <motion.div
                animate={{ x: ['-100%', '300%'] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                className="h-full w-1/3 rounded-full bg-primary"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}